import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './KakaoCallbackPage.css';
import { consumeOAuthState } from '../utils/oauthState';

/**
 * 카카오 OAuth 콜백 처리 페이지
 * /auth/kakao/callback?code=XXXX 로 리다이렉트됨
 */
const KakaoCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const navigate = useNavigate();
  const { handleKakaoCallback } = useAuth();

  // useEffect 중복 실행 방지 (React Strict Mode 대응)
  const isProcessingRef = useRef(false);
  // 리다이렉트 URL을 컴포넌트 마운트 시 즉시 저장 (useEffect 실행 전)
  const savedRedirectUrl = useRef<string | null>(localStorage.getItem('redirect_after_login'));

  useEffect(() => {
    // 이미 처리 중이면 스킵
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // 에러 파라미터가 있는 경우
      if (errorParam) {
        setError(errorDescription || '카카오 로그인이 취소되었습니다.');
        setProcessing(false);
        return;
      }

      // 인증 코드가 없는 경우
      if (!code) {
        setError('인증 코드가 없습니다. 다시 시도해주세요.');
        setProcessing(false);
        return;
      }

      // 이 브라우저에서 시작한 로그인인지 확인 (state 1회용 대조, 2026-10-05)
      if (!consumeOAuthState(searchParams.get('state'))) {
        setError('로그인 요청이 만료되었거나 다른 창에서 시작되었어요. 다시 로그인해 주세요.');
        setProcessing(false);
        return;
      }

      try {
        // localStorage에서 마케팅 동의 정보 읽기
        const consentMarketing = localStorage.getItem('consent_marketing') === 'true';

        // 백엔드로 코드 전송하여 토큰 교환 및 로그인 처리
        await handleKakaoCallback(code, consentMarketing);

        // 로그인 성공 시 이전 페이지로 이동 (저장된 URL이 있으면)
        // useRef에 저장된 값 사용 (Strict Mode 대응)
        const redirectUrl = savedRedirectUrl.current;
        localStorage.removeItem('redirect_after_login');

        const finalUrl = redirectUrl || '/';
        navigate(finalUrl, { replace: true });
      } catch (err: any) {
        console.error('Kakao callback error:', err);
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          '로그인 처리 중 오류가 발생했습니다.';
        setError(errorMessage);
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, handleKakaoCallback, navigate]);

  // 에러 발생 시 표시
  if (error) {
    return (
      <div className="callback-page">
        <div className="callback-container">
          <div className="callback-error">
            <div className="error-icon">⚠️</div>
            <h2>로그인 실패</h2>
            <p>{error}</p>
            <button
              className="retry-btn"
              onClick={() => navigate('/login', { replace: true })}
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 처리 중 로딩 표시
  return (
    <div className="callback-page">
      <div className="callback-container">
        <div className="callback-loading">
          <div className="spinner"></div>
          <p>카카오 로그인 처리 중...</p>
        </div>
      </div>
    </div>
  );
};

export default KakaoCallbackPage;
